import { isResponsive } from '@techchange/filestack';

import { RESOURCE_TYPES } from '../../../constants';

export const RESOURCE_FORM_DATA_DISPATCH = {
	ADD_TRANSLATION: 'ADD_TRANSLATION',
	DELETE_TRANSLATION: 'DELETE_TRANSLATION',
	UPDATE_DATA: 'UPDATE_DATA',
	UPDATE_IMAGE_SOURCES: 'UPDATE_IMAGE_SOURCES',
	UPDATE_LANGUAGE: 'UPDATE_LANGUAGE',
	UPDATE_RESOURCE_CONTENTS: 'UPDATE_RESOURCE_CONTENTS',
	UPDATE_BULK: 'UPDATE_BULK',
	UPDATE_WIDTH: 'UPDATE_WIDTH',
};

/**
 * Set of functions that update the resource form state for
 * a given action type.
 */
export const resourceFormDataReducers = {
	[RESOURCE_FORM_DATA_DISPATCH.ADD_TRANSLATION]: addTranslation,
	[RESOURCE_FORM_DATA_DISPATCH.DELETE_TRANSLATION]: deleteTranslation,
	[RESOURCE_FORM_DATA_DISPATCH.UPDATE_DATA]: updateData,
	[RESOURCE_FORM_DATA_DISPATCH.UPDATE_IMAGE_SOURCES]: updateImageSources,
	[RESOURCE_FORM_DATA_DISPATCH.UPDATE_LANGUAGE]: updateLanguage,
	[RESOURCE_FORM_DATA_DISPATCH.UPDATE_RESOURCE_CONTENTS]: updateResourceContents,
	[RESOURCE_FORM_DATA_DISPATCH.UPDATE_BULK]: updateFields,
	[RESOURCE_FORM_DATA_DISPATCH.UPDATE_WIDTH]: updateWidth,
};

/**
 * Adds a new resource content object to the form data state
 * for the currently selected language.
 *
 * @param {Object} formData  The current form data state.
 * @param {Object} action  Data related to the requested action.
 * @param {Object} action.defaultContent  The default values for a new resource content record.
 *
 * @returns {Object}  The updated form data state.
 */
function addTranslation(formData, action) {

	const { defaultContent } = action;

	// Check that language does not already exist in resource content state
	const existingTranslation = formData.resource_contents.find((rContent) =>
		rContent.language === formData.language);

	if (!existingTranslation) {

		const updatedResourceContents = [...formData.resource_contents];
		const updatedDeletedResourceContents = [...formData.deleted_resource_contents];

		// Check if translation was set to be deleted
		const deletedTranslationIdx = formData.deleted_resource_contents.findIndex((rContent) =>
			rContent.language === formData.language);

		if (deletedTranslationIdx >= 0) {
			updatedDeletedResourceContents.splice(deletedTranslationIdx, 1);
		}

		updatedResourceContents.push({
			language: formData.language,
			// NOTE: will depend on resource type
			...defaultContent,
		});

		return {
			...formData,
			resource_contents: updatedResourceContents,
			deleted_resource_contents: updatedDeletedResourceContents,
		};

	}

	return formData;

}

/**
 * Removes a resource content object from the form data state
 * for the currently selected language and adds it to the list of
 * resource contents to delete.
 *
 * @param {Object} formData  The current form data state.
 *
 * @returns {Object}  The updated form data state.
 */
function deleteTranslation(formData) {

	const updatedResourceContents = [...formData.resource_contents];
	const updatedResourceContentsToDelete = [...formData.deleted_resource_contents];

	const activeTranslationIdx = formData.resource_contents
		.findIndex((rContent) => rContent.language === formData.language);

	const activeTranslation = formData.resource_contents[activeTranslationIdx];

	// Remove active translation from current set of resource contents
	updatedResourceContents.splice(activeTranslationIdx, 1);

	// Add active translation to list of resource contents to delete
	updatedResourceContentsToDelete.push(activeTranslation);

	return {
		...formData,
		resource_contents: updatedResourceContents,
		deleted_resource_contents: updatedResourceContentsToDelete,
	};

}

/**
 * Determines the updated state to reflect the new data value. This includes
 * the fields on the resource itself as well as the resource
 * content object corresponding to the currently selected language.
 *
 * @param {Object} formData  The current form data state.
 * @param {Object} action  Data related to the requested action.
 * @param {String} action.fieldValue  The new value of the field to update.
 *
 * @returns {Object}  The updated form data state.
 */
function updateFields(formData, action) {

	const { fieldValue: updates, defaultLanguage } = action;

	const acceptedFieldNames = [
		'title',
		'content',
		'data',
	];

	const updatedFormData = { ...formData };
	const selectedLanguage = updatedFormData.language;

	Object.keys(updates).forEach((fieldName) => {

		if (!acceptedFieldNames.includes(fieldName)) {
			return;
		}

		let fieldValue = updates[fieldName];

		if (fieldName === 'data') {

			const activeTranslation = updatedFormData.resource_contents
				.find((rContent) => rContent.language === selectedLanguage);

			fieldValue = {
				...activeTranslation.data,
				...fieldValue,
			};

		}

		if (selectedLanguage === defaultLanguage) {
			updatedFormData[fieldName] = fieldValue;
		}

		const updatedResourceContents =
			getUpdatedResourceContents(updatedFormData, fieldName, fieldValue, selectedLanguage);

		updatedFormData.resource_contents = updatedResourceContents;

	});

	return updatedFormData;

}

/**
 * Updates the state to the selected language and loads the resource content for the
 * newly selected language if the data is not already loaded.
 *
 * @param {Object} formData  The current form data state.
 * @param {Object} action  Data related to the requested action.
 * @param {String} action.id  The ID of the resource (used to fetch the requested
 *                            translation if needed).
 * @param {Boolean} action.isNew  Flag indicating if new resource is being created.
 * @param {String} action.fieldValue  The new value of the field to update.
 * @param {Function} action.onLanguageChange  Callback to fetch the requested language.
 *
 * @returns {Object}  The updated form data state.
 */
async function updateLanguage(formData, action) {

	const { fieldValue, id, isNew, onLanguageChange } = action;

	const updatedResourceContents = [...formData.resource_contents];

	// Check to see if a translation exists for the language
	const translation = formData.resource_contents
		.find((rContent) => rContent.language === fieldValue);

	// Check to see if translation has been set to be deleted
	const translationDeleted = formData.deleted_resource_contents
		.find((rContent) => rContent.language === fieldValue);

	let translationContent = translation;

	// Load the translation (if any) if not already loaded
	if (!translationContent && !translationDeleted && !isNew) {

		try {

			const { data: loadedResource } = await onLanguageChange(id, fieldValue);

			if (!loadedResource) {
				throw new Error('Loaded resource undefined');
			}

			// Check that slide has existing content for the requested language...
			translationContent = loadedResource.resource_contents.find((rContent) => {
				return rContent.language === fieldValue;
			});

			// If a translation exists, add it to the form state
			if (translationContent) {
				updatedResourceContents.push({
					language: fieldValue,
					// NOTE: this will be different depending on the resource type!
					title: translationContent.title,
					content: translationContent.content,
					data: translationContent.data,
				});
			}

		} catch (error) {

			// NOTE: error message handled in withResourceLogic
			console.error(error);
			return formData;

		}

	}

	const updatedFormData = {
		...formData,
		language: fieldValue,
		resource_contents: updatedResourceContents,
	};

	return updatedFormData;

}

/**
 * Determines the updated state to reflect the field value for an attribute of
 * a resource content object.
 *
 * NOTE: We are temporarily keeping the "title" and "content" fields on the Resource
 * model in-sync with the English translation until we are ready to remove these fields
 * from the Resource model completely.
 *
 * @param {Object} formData  The current form data state.
 * @param {Object} action  Data related to the requested action.
 * @param {String} action.fieldName  The name of the field to update.
 * @param {String} action.fieldValue  The new value of the field to update.
 *
 * @returns {Object}  The updated form data state.
 */
function updateResourceContents(formData, action) {

	const { fieldName, fieldValue, defaultLanguage } = action;

	const updatedFormData = { ...formData };

	if (formData.language === defaultLanguage) {
		updatedFormData[fieldName] = fieldValue;
	}

	const updatedResourceContents =
		getUpdatedResourceContents(formData, fieldName, fieldValue, formData.language);

	updatedFormData.resource_contents = updatedResourceContents;

	return updatedFormData;

}

/**
 * Determines the updated state to reflect the new width value.
 *
 * @param {Object} formData  The current form data state.
 * @param {Object} action  Data related to the requested action.
 * @param {String} action.fieldValue  The new value of the field to update.
 *
 * @returns {Object}  The updated form data state.
 */
function updateWidth(formData, action) {

	const { fieldValue } = action;

	const updatedFormData = {
		...formData,
		width: parseInt(fieldValue, 10),
	};

	return updatedFormData;

}

/**
 * Determines the updated state to reflect the new data value. This includes
 * the "data" field on the resource itself as well as the resource
 * content object corresponding to the currently selected language.
 *
 * @param {Object} formData  The current form data state.
 * @param {Object} action  Data related to the requested action.
 * @param {String} action.fieldName  The name of the field to update.
 * @param {String} action.fieldValue  The new value of the field to update.
 *
 * @returns {Object}  The updated form data state.
 */
function updateData(formData, action) {

	const { fieldName, fieldValue, defaultLanguage } = action;

	const updatedFormData = { ...formData };

	const selectedLanguage = updatedFormData.language;
	const activeTranslation = updatedFormData.resource_contents
		.find((rContent) => rContent.language === selectedLanguage);

	let updatedData;

	if (fieldName === 'data') {

		updatedData = {
			...activeTranslation.data,
			...fieldValue,
		};

	} else {

		updatedData = {
			...activeTranslation.data,
			[fieldName]: fieldValue,
		};

	}

	const updatedResourceContents =
		getUpdatedResourceContents(
			updatedFormData,
			'data',
			updatedData,
			selectedLanguage
		);

	if (selectedLanguage === defaultLanguage) {
		updatedFormData.data = updatedData;
	}

	updatedFormData.resource_contents = updatedResourceContents;

	return updatedFormData;

}

/**
 * Determines the updated state after adding a "&responsive=true" flag to image
 * sources. This includes the "content" and "data.src" fields on the resource itself
 * as well as the resource content objects.
 *
 * @param {Object} formData  The current form data state.
 * @param {Object} action  Data related to the requested action.
 * @param {String} action.resourceType  The type of the resource.
 *
 * @returns {Object}  The updated form data state.
 */
async function updateImageSources(formData, action) {

	const { resourceType, defaultLanguage } = action;

	let updatedFormData = { ...formData };

	try {
		// Add responsive image sources
		if (resourceType === RESOURCE_TYPES.IMAGE) {

			updatedFormData = await updateDataImageSources(updatedFormData, defaultLanguage);

		} else if (resourceType === RESOURCE_TYPES.SLIDE) {

			updatedFormData = await updateContentImageSources(updatedFormData);

		}

		return updatedFormData;

	} catch (error) {

		console.error(error);
		return updatedFormData;

	}

}

/**
 * Determines the update resource contents list given a change to one of
 * its fields for a given language.
 *
 * @param {Object} formData  The current form data.
 * @param {String} fieldName  The name of the field to update ("title", "content", or "data").
 * @param {String} fieldValue  The new value of the field to update.
 * @param {String} language  The language of the resource content to update.
 *
 * @returns {Array}  A copy of the updated resource contents list.
 */
function getUpdatedResourceContents(formData, fieldName, fieldValue, language) {

	const updatedResourceContents = [...formData.resource_contents];

	const activeTranslationIdx = updatedResourceContents
		.findIndex((rContent) => rContent.language === language);

	const activeTranslation = updatedResourceContents[activeTranslationIdx];

	if (activeTranslationIdx >= 0) {
		updatedResourceContents[activeTranslationIdx] = {
			...activeTranslation,
			[fieldName]: fieldValue,
		};
	}

	return updatedResourceContents;

}

/**
 * Checks the "src" fields on formData and within formData.resource_contents.
 * Adds the "responsive" flag as needed.
 *
 * @param {Object} formData  The form data.
 * @param {String} defaultLanguage  The default language for the product.
 *
 * @returns {Object} A new copy of the updated form data.
 */
async function updateDataImageSources(formData, defaultLanguage) {

	const updatedFormData = { ...formData };
	const updatedResourceContents = [];

	for (let i = 0; i < updatedFormData.resource_contents.length; i++) {

		const updatedResourceContent = {
			...formData.resource_contents[i],
		};

		const src = updatedResourceContent.data.src;

		// // Check responsive flag for filestack images
		if (src.includes('cdn.filestackcontent.com') && !src.includes('&responsive=')) {

			try {

				// Add flag to image source
				const result = await isResponsive(src);

				const newSrc = `${src}&responsive=${result}`;

				updatedResourceContent.data = {
					...updatedResourceContent.data,
					src: newSrc,
				};

				if (updatedResourceContent.language === defaultLanguage) {
					updatedFormData.data = {
						...updatedFormData.data,
						src: newSrc,
					};
				}

			} catch (error) {

				// Keep state as is without src flags if we run into an error
				console.error(error);

			}

		}

		updatedResourceContents.push(updatedResourceContent);

	}

	updatedFormData.resource_contents = updatedResourceContents;

	return updatedFormData;

}

/**
 * Checks the "src" attributes on image nodes in formData.content and within
 * formData.resource_contents. Adds the "responsive" flag as needed.
 *
 * @param {Object} formData  The form data.
 *
 * @returns {Object} A new copy of the updated form data.
 */
async function updateContentImageSources(formData) {

	const updatedFormData = { ...formData };

	const updateImageSourcesHelper = (node, promises) => {

		if (node?.type === 'image') {

			const { src } = node.attrs;

			// Check responsive flag for filestack images
			if (src.includes('cdn.filestackcontent.com') && !src.includes('&responsive=')) {

				promises.push(new Promise((resolve) => {

					// Add flag to image source
					isResponsive(src).then((result) => {

						const newSrc = `${src}&responsive=${result}`;

						node.attrs.src = newSrc;

						return resolve();

					});

				}));

			}

		}

		// Recurse to children
		if (node?.content) {
			node.content.forEach((child) => updateImageSourcesHelper(child, promises));
		}

		return promises;

	};

	const updatedResourceContents = [...updatedFormData.resource_contents];

	for (let i = 0; i < updatedResourceContents.length; i++) {

		const resourceContent = updatedResourceContents[i];

		const updatedResourceContentContent = { ...resourceContent.content };

		await Promise.all(updateImageSourcesHelper(updatedResourceContentContent, []));

		updatedResourceContents[i] = {
			...resourceContent,
			content: updatedResourceContentContent,
		};

	}

	updatedFormData.resource_contents = updatedResourceContents;

	return updatedFormData;

}
